import { Icon, Skeleton, Stack, SxProps, Theme, Tooltip, Typography } from '@mui/material';
import { SystemStyleObject } from '@mui/system';
import {
  ApiError,
  AttendanceRegisterForSchool,
  AttendanceSessionEnum,
  exportFireRegister,
  FrequencyTypeEnum,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { useNotifications } from '@schooly/components/notifications';
import { SchoolUserRole } from '@schooly/constants';
import { useAgeGroups } from '@schooly/hooks/use-school-properties';
import { FireIcon, Spin } from '@schooly/style';
import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { downloadFile } from '../../../utils/downloadFile';
import { AttendanceExportButton, AttendanceExportButtonProps } from './AttendanceExport.styled';

export interface AttendanceRegisterExportProps extends AttendanceExportButtonProps {
  register: AttendanceRegisterForSchool;
  sx?: SxProps<Theme>;
}

const hasPmFrequency = (frequency?: FrequencyTypeEnum | null) =>
  frequency === FrequencyTypeEnum.AmPm || frequency === FrequencyTypeEnum.Pm;

export const AttendanceRegisterExport: FC<AttendanceRegisterExportProps> = ({
  register,
  sx,
  ...props
}) => {
  const { formatMessage } = useIntl();
  const { schoolId = '' } = useAuth();
  const { activeAgeGroups, isLoading: isLoadingAgeGroups } = useAgeGroups(
    { schoolId, userType: SchoolUserRole.Student },
    { enabled: !!schoolId },
  );

  const sessions = useMemo(() => {
    const showPm = activeAgeGroups.some((ageGroup) =>
      hasPmFrequency(ageGroup.attendance_frequency?.frequency),
    );

    return showPm
      ? [AttendanceSessionEnum.Am, AttendanceSessionEnum.Pm]
      : [AttendanceSessionEnum.Am];
  }, [activeAgeGroups]);

  const groupCountBySession = useMemo(() => {
    const counts = new Map<AttendanceSessionEnum, number>();

    register.sessions?.forEach(({ session, group_count }) => {
      counts.set(session, group_count);
    });

    return counts;
  }, [register.sessions]);

  if (isLoadingAgeGroups) {
    return (
      <Stack direction="row" gap={1} justifyContent="flex-end" width="100%">
        <Skeleton variant="rectangular" width={65} height={30} />
        <Skeleton variant="rectangular" width={65} height={30} />
      </Stack>
    );
  }

  const isSingleButton = sessions.length === 1;

  return (
    <Stack direction="row" gap={1} justifyContent="flex-end" width="100%">
      {sessions.map((session) => {
        const isDisabled = (groupCountBySession.get(session) ?? 0) === 0;

        return (
          <Tooltip
            key={session}
            title={
              isDisabled
                ? formatMessage(
                    { id: 'attendance-FireRegister-NoGroupsTooltip' },
                    { session: formatMessage({ id: `attendance-${session.toUpperCase()}` }) },
                  )
                : ''
            }
          >
            <div>
              <AttendanceRegisterExportButton
                session={session}
                registerDate={register.register_date}
                disabled={isDisabled}
                showLabel={!isSingleButton}
                sx={sx}
                {...props}
              />
            </div>
          </Tooltip>
        );
      })}
    </Stack>
  );
};

interface AttendanceRegisterExportButtonProps extends AttendanceExportButtonProps {
  session: AttendanceSessionEnum;
  registerDate: string;
  disabled?: boolean;
  showLabel?: boolean;
  sx?: SxProps<Theme>;
}

const AttendanceRegisterExportButton: FC<AttendanceRegisterExportButtonProps> = ({
  session,
  registerDate,
  disabled,
  showLabel,
  sx,
  ...props
}) => {
  const { formatMessage } = useIntl();
  const { schoolId = '' } = useAuth();
  const { showError } = useNotifications();
  const [isFetching, setIsFetching] = useState(false);
  const isFetchingRef = useRef(false);

  const handleClick = useCallback<React.MouseEventHandler>(
    async (event) => {
      event.stopPropagation();

      if (isFetchingRef.current) {
        return;
      }

      isFetchingRef.current = true;
      setIsFetching(true);

      try {
        const data = await exportFireRegister({
          school_id: schoolId,
          session,
          date: registerDate,
        });
        const filename = `${formatMessage({
          id: 'attendance-Register',
        })}.${registerDate}.${formatMessage({
          id: `attendance-${session.toUpperCase()}`,
        })}.pdf`;
        downloadFile(data, filename);
      } catch (error) {
        showError(error as ApiError);
      }

      isFetchingRef.current = false;
      setIsFetching(false);
    },
    [formatMessage, registerDate, schoolId, session, showError],
  );

  return (
    <AttendanceExportButton
      onClick={handleClick}
      disabled={disabled}
      sx={(theme) => ({
        ...(!showLabel && { px: 0.75 }),
        ...((typeof sx === 'function' ? sx(theme) : (sx ?? {})) as SystemStyleObject),
      })}
      {...props}
    >
      <Icon fontSize="small">{isFetching ? <Spin /> : <FireIcon />}</Icon>
      {showLabel && (
        <Typography component="span" color="inherit">
          {formatMessage({ id: `attendance-${session.toUpperCase()}` })}
        </Typography>
      )}
    </AttendanceExportButton>
  );
};
